import React from "react";
import {
  BookOpen,
  Package,
  FileText,
  Tag,
  Receipt,
  Envelope,
  UsersThree,
  Newspaper,
  Gift,
  Warning,
  Info,
  Download,
} from "@phosphor-icons/react";

/**
 * Admin operations guide. Rendered as a rich HTML document inside the admin dashboard.
 * All content lives here so admins can bookmark /admin and read it without leaving the app.
 */
export default function AdminGuide() {
  return (
    <div className="max-w-[900px]">
      {/* Header */}
      <div className="border border-white/10 bg-gradient-to-br from-[#120704] via-[#0d0d0d] to-[#0a1512] p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <BookOpen size={24} weight="duotone" className="text-[#FFC900]" />
          <div>
            <div className="font-label text-[10px] text-[#FFC900]">/ ADMIN GUIDE</div>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tighter sm:text-3xl">
              How to run Vape Essence
            </h2>
          </div>
        </div>
        <p className="mt-4 text-sm text-white/70 leading-relaxed">
          Everything you can manage from this dashboard — products, discounts, orders,
          content, newsletters, referrals, articles and bundles. Bookmark this page.
        </p>
        <div className="mt-4 grid gap-2 text-[11px] font-label text-white/60 sm:grid-cols-2">
          <div>Last updated: February 2026</div>
          <div>Preview URL: <span className="text-[#00FFC2]">/admin</span></div>
        </div>
      </div>

      {/* Quick nav */}
      <nav className="mt-8 grid gap-2 sm:grid-cols-2 md:grid-cols-3">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="flex items-center gap-2 border border-white/10 bg-[#0d0d0d] px-3 py-2 text-xs hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
          >
            <s.Icon size={14} weight="bold" /> {s.label}
          </a>
        ))}
      </nav>

      {/* Signed-in banner */}
      <Note kind="info">
        You must be signed in as an admin (<code>admin@vapeessence.co.za</code>) to see the
        Admin Dashboard. Log in at <code>/admin/login</code>.
      </Note>

      {/* --- 1. PRODUCTS --- */}
      <Section id="products" Icon={Package} title="1 · Products (add / edit / remove)">
        <p>
          The <b>Products</b> tab is where you manage everything customers see in the
          <a href="/shop" className="text-[#00FFC2] hover:underline"> Shop</a>. You can
          add products one-by-one, or in bulk with an Excel spreadsheet.
        </p>

        <h4>Add a single product</h4>
        <ol>
          <li>Go to <b>Admin → Products</b>.</li>
          <li>Click <Btn>+ ADD PRODUCT</Btn> at the top.</li>
          <li>
            Fill in the fields:
            <ul>
              <li><b>Name</b> — e.g. <i>Elf Bar BC5000 Watermelon Ice</i>.</li>
              <li><b>Slug</b> — auto-generated from the name (leave blank unless you want a specific URL, e.g. <code>elf-bar-bc5000-watermelon</code>).</li>
              <li><b>Brand</b> — e.g. <i>Elf Bar</i>.</li>
              <li><b>Category</b> — pick one: <i>Disposable Vapes</i> or <i>Nicotine Pouches</i> (typing a new value creates a new category).</li>
              <li><b>Description</b> — what shows on the product page.</li>
              <li><b>Price (R)</b> — the price customers pay.</li>
              <li><b>Compare-at price (R)</b> — the original higher price. If this is <i>greater than</i> the price, a yellow <span className="inline-block bg-[#FFC900] text-black px-1 py-[1px] text-[9px] font-black leading-tight">−%%</span> sale badge appears on the card.</li>
              <li><b>Stock</b> — number of units available. When stock ≤ 5 the card shows <span className="text-[#FF5A1F]">ONLY X LEFT</span>. When stock is 0 the product is out of stock (add-to-cart is disabled).</li>
              <li><b>Featured</b> toggle — puts an orange <span className="inline-block bg-[#FF5A1F] text-black px-1 py-[1px] text-[9px] font-black leading-tight">FEATURED</span> badge on the card and shows it in the "Featured picks" carousel on the Shop page.</li>
              <li><b>Images</b> — upload one or more images. First image is the main thumbnail.</li>
              <li><b>Variants</b> — optional. Add options like <i>Flavour</i> with values <i>[Mango, Blueberry, Mint]</i>. Customers pick one before adding to cart.</li>
            </ul>
          </li>
          <li>Click <Btn>SAVE</Btn>. The product appears immediately on <a href="/shop" className="text-[#00FFC2] hover:underline">/shop</a>.</li>
        </ol>

        <h4>Add many products at once (Excel bulk upload)</h4>
        <ol>
          <li>Click <Btn><Download size={11} weight="bold" className="inline mr-1" />DOWNLOAD TEMPLATE</Btn>. This downloads <code>vape_essence_products_template.xlsx</code>.</li>
          <li>Open the file in Excel or Google Sheets. Each row = one product. Column headers are self-explanatory (<code>name</code>, <code>brand</code>, <code>category</code>, <code>price</code>, <code>compare_at</code>, <code>stock</code>, <code>featured</code>, <code>description</code>, <code>image_urls</code>).</li>
          <li>For <b>image_urls</b>: paste one or more public image URLs separated by <code>|</code> (pipe). Example: <code>https://…/a.jpg|https://…/b.jpg</code>.</li>
          <li>For <b>variants</b>: use the format <code>Flavour:Mango,Blueberry|Strength:20mg,50mg</code>.</li>
          <li>Save the file and click <Btn>UPLOAD EXCEL</Btn>. Everything gets imported. Existing products with the same <b>slug</b> are updated, new ones are inserted.</li>
        </ol>

        <h4>Edit a product</h4>
        <p>
          Click the <PencilInline /> pencil icon next to any product row. Change what you
          need, hit <Btn>SAVE</Btn>. Changes are live instantly (no redeploy).
        </p>

        <h4>Remove a product</h4>
        <p>
          Click the <TrashInline /> trash icon next to the product. Confirm the dialog.
          The product is deleted from the DB and disappears from the shop immediately.
        </p>

        <Note kind="warn">
          Deleting a product is permanent. If you want to <b>temporarily hide</b> a product
          instead, just set its <b>stock to 0</b> — customers will see "Out of stock" but
          you can restock later without re-creating the listing.
        </Note>

        <h4>Understanding the product badges</h4>
        <ul>
          <li>🟢 <b>NEW</b> — product was created in the last <b>30 days</b> (automatic).</li>
          <li>🟠 <b>FEATURED</b> — the <i>Featured</i> toggle is on.</li>
          <li>🟡 <b>−%</b> (SALE) — <code>compare_at</code> is higher than <code>price</code>.</li>
          <li>🔴 <b>END OF SALE</b> — on sale AND stock ≤ 5 (closing out inventory).</li>
        </ul>
      </Section>

      {/* --- 2. CONTENT --- */}
      <Section id="content" Icon={FileText} title="2 · Site content (Home, About, Contact, Footer)">
        <p>
          The <b>Site Content</b> tab controls all the text that isn't a product. Edit a
          field, click <Btn>SAVE CONTENT</Btn>. Changes go live instantly.
        </p>
        <h4>What you can edit here</h4>
        <ul>
          <li><b>Hero tagline / title / subtitle</b> — the big text on the home page.</li>
          <li><b>About title / body / pillars</b> — the About page copy + the three cards.</li>
          <li><b>Contact email / phone / address</b> — shown on the Contact page and in the footer.</li>
          <li><b>Footer tagline</b> — small legal line at the bottom of every page.</li>
          <li><b>Free-shipping threshold (R)</b> — the "add R X more for free delivery" bar on the Cart. Also drives the shipping perk.</li>
          <li><b>TikTok video URLs</b> — video URLs shown in the TikTok embed section of the home page.</li>
        </ul>
      </Section>

      {/* --- 3. DISCOUNTS --- */}
      <Section id="discounts" Icon={Tag} title="3 · Discounts (promo codes)">
        <p>Create promo codes customers enter at checkout.</p>
        <ol>
          <li>Click <Btn>+ NEW DISCOUNT</Btn>.</li>
          <li>
            Fill in:
            <ul>
              <li><b>Code</b> — what the customer types (e.g. <code>SAVE10</code>).</li>
              <li><b>Percent off</b> <i>or</i> <b>Amount off (R)</b> — pick one.</li>
              <li><b>Expires at</b> — optional date. Leave blank for evergreen.</li>
              <li><b>Description</b> — internal note.</li>
              <li><b>Active</b> — toggle off to disable without deleting.</li>
            </ul>
          </li>
          <li>Save. Customers enter the code on the Cart page.</li>
        </ol>
      </Section>

      {/* --- 4. ORDERS --- */}
      <Section id="orders" Icon={Receipt} title="4 · Orders (fulfilment & tracking)">
        <p>All customer orders appear here in reverse-chronological order.</p>
        <h4>Order states</h4>
        <ul>
          <li><b>pending</b> — created but not paid.</li>
          <li><b>paid</b> — PayFast has confirmed payment (via ITN webhook).</li>
          <li><b>shipped</b> — you've dispatched the parcel. Add a tracking number here.</li>
          <li><b>delivered</b> — customer confirmed delivery (or you marked it manually).</li>
          <li><b>cancelled</b> / <b>failed</b> — self-explanatory.</li>
        </ul>
        <h4>How to fulfil an order</h4>
        <ol>
          <li>Once PayFast confirms payment, the order moves to <b>paid</b> automatically.</li>
          <li>Pack the parcel. Grab the tracking number from your courier.</li>
          <li>In the order row, click <Btn>MARK SHIPPED</Btn> and paste the tracking number. Customer gets an email with the tracking link.</li>
          <li>When delivered, click <Btn>MARK DELIVERED</Btn>.</li>
        </ol>
      </Section>

      {/* --- 5. NEWSLETTER --- */}
      <Section id="newsletter" Icon={Envelope} title="5 · Newsletter (subscribers & campaigns)">
        <p>
          Every visitor who opts in from the site footer or a checkout popup lands here.
          Export the CSV, or use the built-in composer to send a broadcast.
        </p>
        <ul>
          <li><b>Export</b> — downloads all subscriber emails.</li>
          <li><b>Send campaign</b> — subject + HTML body. Every active subscriber receives it via Resend.</li>
          <li><b>Unsubscribe</b> — automatic footer link on every email.</li>
        </ul>
        <Note kind="info">
          Keep your subject line short (&lt; 50 chars) and lead with a benefit — <i>"3 for
          R500 · 48h only"</i> beats <i>"Weekly newsletter"</i> every time.
        </Note>
      </Section>

      {/* --- 6. REFERRALS --- */}
      <Section id="referrals" Icon={UsersThree} title="6 · Referrals & loyalty points">
        <p>
          Every registered customer gets a personal referral code from their profile page.
          When someone new checks out using their code, <b>both accounts earn 50 loyalty
          points</b>.
        </p>
        <h4>How points work</h4>
        <ul>
          <li>Customers earn points equal to <b>5% of their order value (excluding VAT)</b> on paid orders.</li>
          <li>Points redeem at checkout — <b>100 points = R10 off</b>.</li>
          <li>Tiers: <b>New</b> (&lt; 100), <b>Regular</b> (100 – 499), <b>Elite</b> (≥ 500).</li>
        </ul>
      </Section>

      {/* --- 7. ARTICLES --- */}
      {/* --- 7. BUNDLES --- */}
      <Section id="bundles" Icon={Gift} title="7 · Bundles (multi-product packages)">
        <p>
          Bundles are curated combos, e.g. the <i>Starter Kit</i> (1 vape + 1 pouch tin at
          a discount).
        </p>
        <ol>
          <li>Click <Btn>+ NEW BUNDLE</Btn>.</li>
          <li>Pick the products to include, set a bundle price. The MSRP (sum of individual prices) auto-calculates so customers see how much they save.</li>
          <li>Save. The bundle shows in the "You may also like" strip on product pages, plus its own <code>/shop?cat=Bundles</code> filter.</li>
        </ol>
      </Section>

      {/* --- 9. TIPS --- */}
      <Section id="tips" Icon={Info} title="8 · Pro tips">
        <ul>
          <li><b>Featured products</b> — Keep the number of Featured items to 6–10. More than that and the "Featured picks" carousel becomes noise.</li>
          <li><b>Sales</b> — When running a promo, set <code>compare_at</code> BEFORE dropping <code>price</code>, not the other way round. The yellow % badge is calculated live.</li>
          <li><b>Photo quality</b> — Use PNGs or high-res JPGs on a plain background. First image is the card thumbnail, so pick your best shot.</li>
          <li><b>Content changes</b> — always click <b>SAVE</b> at the bottom of a tab; there is no autosave.</li>
          <li><b>PayFast sandbox vs live</b> — the backend auto-switches based on the Merchant ID in <code>backend/.env</code>. Never test with real cards until you flip to Live.</li>
          <li><b>Backing up</b> — DB backups are handled by Emergent hosting. Product images are stored in Object Storage and survive redeploys.</li>
        </ul>
      </Section>

      {/* --- FAQ --- */}
      <Section id="faq" Icon={Info} title="9 · FAQ">
        <h4>My product doesn't appear on the shop page.</h4>
        <p>
          Check: (a) <b>Stock</b> is 0 → shows as "Out of stock" but still visible. (b) Is the
          category typed correctly (case matters)? (c) Refresh the shop page — no autoreload for
          admin-side changes on a customer's open tab.
        </p>

        <h4>I updated a product but the site still shows the old value.</h4>
        <p>
          Hard-refresh your browser (<kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>R</kbd> on Windows,
          <kbd>Cmd</kbd>+<kbd>Shift</kbd>+<kbd>R</kbd> on Mac). The frontend is a PWA — the
          service worker occasionally serves cached copies.
        </p>

        <h4>Excel upload failed.</h4>
        <p>
          Common causes: (a) column headers were renamed (must match the template exactly);
          (b) an image URL is malformed; (c) <code>price</code> or <code>stock</code> has a comma
          instead of a dot (<code>249.00</code> not <code>249,00</code>).
        </p>

        <h4>I need to change the logo / colors / age-gate text.</h4>
        <p>
          Those live in code — ping the developer. Everything else on this list you can do
          yourself.
        </p>
      </Section>

      {/* Footer */}
      <div className="mt-10 border-t border-white/10 pt-6 text-[11px] font-label text-white/40">
        Vape Essence · Admin operations guide · Questions? Contact the dev team.
      </div>
    </div>
  );
}

/* ---------- Building blocks ---------- */

const SECTIONS = [
  { id: "products", label: "Products", Icon: Package },
  { id: "content", label: "Site Content", Icon: FileText },
  { id: "discounts", label: "Discounts", Icon: Tag },
  { id: "orders", label: "Orders", Icon: Receipt },
  { id: "newsletter", label: "Newsletter", Icon: Envelope },
  { id: "referrals", label: "Referrals", Icon: UsersThree },
  { id: "bundles", label: "Bundles", Icon: Gift },
  { id: "tips", label: "Pro tips", Icon: Info },
  { id: "faq", label: "FAQ", Icon: Info },
];

function Section({ id, Icon, title, children }) {
  return (
    <section id={id} className="mt-10 border-t border-white/10 pt-8 scroll-mt-24 admin-guide-prose">
      <div className="flex items-center gap-3 mb-4">
        <Icon size={18} weight="duotone" className="text-[#FF5A1F]" />
        <h3 className="font-display text-xl font-black tracking-tighter sm:text-2xl">{title}</h3>
      </div>
      <div className="prose-body">{children}</div>
    </section>
  );
}

function Note({ kind = "info", children }) {
  const map = {
    info: {
      Icon: Info,
      border: "border-[#00FFC2]/40",
      bg: "bg-[#00FFC2]/5",
      iconColor: "text-[#00FFC2]",
    },
    warn: {
      Icon: Warning,
      border: "border-[#FFC900]/40",
      bg: "bg-[#FFC900]/5",
      iconColor: "text-[#FFC900]",
    },
  };
  const s = map[kind];
  return (
    <div className={`mt-4 flex items-start gap-3 border ${s.border} ${s.bg} p-3 text-sm text-white/80`}>
      <s.Icon size={16} weight="duotone" className={`${s.iconColor} mt-[2px] shrink-0`} />
      <div>{children}</div>
    </div>
  );
}

function Btn({ children }) {
  return (
    <span className="inline-flex items-center gap-1 border border-white/25 bg-white/5 px-2 py-[2px] font-label text-[10px] font-bold text-white align-middle">
      {children}
    </span>
  );
}

function PencilInline() {
  return (
    <span className="inline-flex h-5 w-5 items-center justify-center border border-white/20 align-middle">
      <span className="text-[9px]">✎</span>
    </span>
  );
}

function TrashInline() {
  return (
    <span className="inline-flex h-5 w-5 items-center justify-center border border-white/20 align-middle text-red-400">
      <span className="text-[9px]">🗑</span>
    </span>
  );
}
